import { useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { selectAction, selectAuthPanel } from '@/redux/nodes/actions';
import { AuthPanel } from '@/shared/types/node';
import { RootState } from '@/shared/types/state';

const STEP_TO_PANEL: Record<string, AuthPanel> = {
  identification: 'step-identification',
  verification: 'step-verification',
};

/**
 * Consumes a deep-link `?action=&step=&outcome=&question=` on the Dialog page.
 * Once the target dialog's nodes are in redux, selects the sub_agent action and
 * routes to the requested panel — the step panel, or (when `outcome` is
 * success/failure, e.g. from the delete-node warning) the matching
 * success/failure dropdown. Strips `action`/`step`/`outcome` (we keep
 * `question` for the step panel to handle scroll + highlight on mount).
 */
export const useAuthDeepLink = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const nodesLoaded = useSelector(
    (state: RootState) => state.nodes.dialogId !== null && state.nodes.nodes.length > 0
  );
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const actionId = searchParams.get('action');
    const step = searchParams.get('step');
    const outcome = searchParams.get('outcome');
    if (!actionId) {
      // Params are gone (including right after our own strip below). Re-arm so
      // clicking the same deep-link again — e.g. re-opening the delete-node
      // warning for the same target — fires it once more instead of being
      // swallowed by the dedup guard.
      handledRef.current = null;
      return;
    }
    if (!nodesLoaded) {
      return;
    }
    const key = `${actionId}:${step ?? ''}:${outcome ?? ''}`;
    if (handledRef.current === key) {
      return;
    }
    handledRef.current = key;

    const stepKey = step === 'verification' ? 'verification' : 'identification';
    let panel: AuthPanel;
    if (outcome === 'success' || outcome === 'failure') {
      panel = `${outcome}-${stepKey}`;
    } else {
      panel = (step && STEP_TO_PANEL[step]) || 'step-identification';
    }
    dispatch(selectAction({ actionId, authPanel: panel }));
    dispatch(selectAuthPanel({ panel }));

    // Drop the deep-link params; keep `question` so the step panel can scroll
    // + briefly highlight the matching question card on mount.
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.delete('step');
    next.delete('outcome');
    setSearchParams(next, { replace: true });
  }, [dispatch, nodesLoaded, searchParams, setSearchParams]);
};
